"use client";
/**
 * Exit-intent "connect with the team" popup — ported from v6 team-connect.tsx.
 * Design/markup is v6's (.team-connect, styled by the imported v6 CSS); Base-UI
 * Button/Input/Popover replaced with plain elements + a state-toggled form.
 * Shows once per session on a top-edge exit (fine-pointer only), dismiss stored
 * 7 days. Excluded on /get-contracted and /contact-us. Mailto submit.
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

const STORAGE = "abm-team-connect";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

const ArrowUpRight = ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7" /><path d="M7 7h10v10" /></svg>
);

export default function TeamConnect() {
    const pathname = usePathname();
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [feedback, setFeedback] = useState("");
    const excluded =
        (pathname ?? "").startsWith("/get-contracted") ||
        pathname === "/contact-us";

    useEffect(() => {
        if (
            excluded ||
            !window.matchMedia("(hover: hover) and (pointer: fine)").matches
        )
            return;
        try {
            if (
                Number(localStorage.getItem(`${STORAGE}-dismissed`)) >
                    Date.now() ||
                sessionStorage.getItem(`${STORAGE}-shown`)
            )
                return;
        } catch {
            /* still works without storage */
        }
        let hasEnteredPage = false;
        let shown = false;
        const trackPointer = (event: MouseEvent) => {
            if (event.clientY > 40) hasEnteredPage = true;
        };
        const onExit = (event: MouseEvent) => {
            const typing = document.activeElement?.matches(
                'input, textarea, select, [contenteditable="true"]',
            );
            if (
                shown ||
                !hasEnteredPage ||
                event.relatedTarget !== null ||
                event.clientY > 0 ||
                document.hidden ||
                typing ||
                document.querySelector('[role="dialog"], [role="alertdialog"]')
            )
                return;
            shown = true;
            setVisible(true);
            try {
                sessionStorage.setItem(`${STORAGE}-shown`, "1");
            } catch {
                /* optional */
            }
        };
        document.addEventListener("mousemove", trackPointer, { passive: true });
        document.addEventListener("mouseout", onExit, { passive: true });
        return () => {
            document.removeEventListener("mousemove", trackPointer);
            document.removeEventListener("mouseout", onExit);
        };
    }, [excluded]);

    function dismiss() {
        setExpanded(false);
        setVisible(false);
        try {
            localStorage.setItem(
                `${STORAGE}-dismissed`,
                String(Date.now() + DISMISS_MS),
            );
        } catch {
            /* optional */
        }
    }

    if (!visible || excluded) return null;
    return (
        <aside className="team-connect" aria-label="Connect with the Agent Boost team">
            <button
                type="button"
                className="team-dismiss"
                aria-label="Dismiss team invitation"
                onClick={dismiss}
            >
                ✕
            </button>
            <span className="mono team-eyebrow">◌ A REAL PERSON. RIGHT HERE.</span>
            <h2>Want to talk it through?</h2>
            <p>
                Connect with somebody on our team.
                <br />A quick question is a great place to start.
            </p>
            <button
                type="button"
                className="team-trigger"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
            >
                Let&rsquo;s connect <ArrowUpRight size={18} />
            </button>
            {expanded && (
                <div className="team-form-panel">
                    <div className="team-form-title mono">LET&rsquo;S MAKE AN INTRO.</div>
                    <p className="team-form-description">
                        Leave an email or phone number for the team to reach you.
                    </p>
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            const data = new FormData(event.currentTarget);
                            const email = String(data.get("email") ?? "").trim();
                            const phone = String(data.get("phone") ?? "").trim();
                            if (!email && !phone) {
                                setFeedback(
                                    "Add an email or phone number so the team can reach you.",
                                );
                                return;
                            }
                            const body = `Hi Agent Boost team,\n\nI'd like to connect with somebody on your team.\n\nEmail: ${email || "Not provided"}\nPhone: ${phone || "Not provided"}\n`;
                            window.location.href = `mailto:info@agentboost.com?subject=${encodeURIComponent("Let's connect — Agent Boost")}&body=${encodeURIComponent(body)}`;
                            setFeedback(
                                "Finish sending in your email app. If it didn't open, email info@agentboost.com or call 855.378.1451.",
                            );
                        }}
                    >
                        <label htmlFor="team-connect-email">
                            Email
                            <input
                                id="team-connect-email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                placeholder="you@example.com"
                            />
                        </label>
                        <label htmlFor="team-connect-phone">
                            Phone
                            <input
                                id="team-connect-phone"
                                name="phone"
                                type="tel"
                                autoComplete="tel"
                                placeholder="Your phone number"
                            />
                        </label>
                        <button type="submit" className="team-submit">
                            Open email draft ✉
                        </button>
                        <p className="team-form-note">
                            Opens your email app. Review and send from there.
                        </p>
                        {feedback && (
                            <output className="team-feedback">{feedback}</output>
                        )}
                    </form>
                    <a className="team-call" href="tel:+18553781451">
                        Prefer a call? 855.378.1451 ↗︎
                    </a>
                </div>
            )}
        </aside>
    );
}
